import React, { useEffect, useRef, useState } from 'react';

import VersionList from 'shared/components/ui/VersionList';
import { authorName, shortHash, timeAgo } from 'features/prompts/lib/promptMembers';

/**
 * Version history of a prompt, newest first: a popover under the History button
 * (same as the eval Versions popover). Versions are named by their hash; a row
 * opens the detail popup (view / load / diff).
 */
export default function VersionTimeline({ versions, members, onSelect, onClose }) {
  const boxRef = useRef(null);
  const [pos, setPos] = useState({ top: 52, right: 16 });
  useEffect(() => {
    const t = document.querySelector('[data-versions-trigger]');
    if (t) {
      const r = t.getBoundingClientRect();
      setPos({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
    }
    const down = (e) => {
      if (boxRef.current?.contains(e.target) || e.target.closest?.('[data-versions-trigger]')) return;
      onClose?.();
    };
    const key = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', down);
    window.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); window.removeEventListener('keydown', key); };
  }, [onClose]);

  return (
    <aside
      aria-label="Version history"
      ref={boxRef}
      style={{ position: 'fixed', top: pos.top, right: pos.right, fontFamily: 'Inter, sans-serif' }}
      className="z-[70] flex max-h-[min(70vh,560px)] w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg"
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        <VersionList
          empty="No versions yet. Save one to start the history."
          onSelect={(it) => onSelect(it.version)}
          items={versions.map((v) => {
            const others = (v.labels || []).filter((l) => l !== 'production');
            return {
              id: v.version_no,
              version: v,
              label: <span className="font-mono">{shortHash(v.version_hash) || 'unversioned'}</span>,
              live: (v.labels || []).includes('production'),
              time: timeAgo(v.created_at),
              author: authorName(v, members),
              note: [v.commit_message, ...others].filter(Boolean).join(' · '),
              actions: [{ key: 'view', label: 'View', onClick: () => onSelect(v) }],
            };
          })}
        />
      </div>
    </aside>
  );
}
